import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { startSentryReplay } from '@/lib/sentry';
import api, { resetAuthPhase } from '@/lib/axios';
import { User } from '@/types/user';
import { Store } from '@/types/store';
import userStoreService, { type SwitchStoreResponse } from '@/services/userStoreService';
import authService from '@/services/authService';
import { subscribeToPushNotifications, requestNotificationPermission, checkNotificationSupport } from '@/utils/notificationPermission';
import ExpiredSubscriptionOverlay from '@/components/ExpiredSubscriptionOverlay';
import SecureStorage from '@/lib/utils/secure-storage';
import { authLogger } from '@/lib/utils/secure-logger';
import { checkInactivityAndValidateSubscription } from '@/utils/notificationPermission';
import { setUser as setSentryUser, buildSentryUserFromSession } from '@/lib/sentry';

interface AuthContextType {
  user: User | null;
  store: Store | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  switchStore: (storeId: string) => Promise<SwitchStoreResponse>;
  isLoading: boolean;
  hasNetworkIssue: boolean;
  subscriptionStatus: 'active' | 'expired' | 'trial' | 'none' | null;
  dismissExpiredOverlay: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Accepts the same loose session shapes as buildSentryUserFromSession so both
// full User/Store objects and partial API payloads (e.g. store-switch) work.
function syncSentryContext(
  user: Parameters<typeof buildSentryUserFromSession>[0],
  store: Parameters<typeof buildSentryUserFromSession>[1]
) {
  setSentryUser(buildSentryUserFromSession(user, store));
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [store, setStore] = useState<Store | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasNetworkIssue, setHasNetworkIssue] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'active' | 'expired' | 'trial' | 'none' | null>(null);
  const [showExpiredOverlay, setShowExpiredOverlay] = useState(false);

  // Retry management for transient network failures
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Function to dismiss the expired subscription overlay
  // This is kept for API compatibility but not used anymore
  const dismissExpiredOverlay = () => {
    // We no longer allow dismissing the overlay without requesting reactivation
    // setShowExpiredOverlay(false);
  };

  // Register background sync to keep service worker alive (critical for iOS notifications)
  // Defined early so it can be used by enableNotificationsAutomatically
  const registerBackgroundSync = async (registration: ServiceWorkerRegistration) => {
    try {
      // Register periodic background sync if supported (keeps SW alive on iOS)
      if ('periodicSync' in registration) {
        try {
          const status = await (registration as any).periodicSync.getTags();
          if (!status.includes('notification-sync')) {
            await (registration as any).periodicSync.register('notification-sync', {
              minInterval: 24 * 60 * 60 * 1000 // 24 hours
            });
            authLogger.debug('Periodic background sync registered for iOS notifications');
          }
        } catch (error) {
          authLogger.debug('Periodic sync registration failed (may require user permission):', error);
        }
      }

      // Register regular background sync as fallback
      if ('sync' in registration) {
        try {
          await (registration as any).sync.register('keep-alive-sync');
          authLogger.debug('Background sync registered to keep service worker alive');
        } catch (error) {
          authLogger.debug('Background sync registration failed:', error);
        }
      }
    } catch (error) {
      authLogger.debug('Background sync not supported:', error);
    }
  };

  // 🔔 AUTOMATIC PUSH NOTIFICATIONS: Enable notifications automatically (required for work app)
  // No permission prompt - automatically enable since notifications are required
  // Defined early so it can be used in validateSession
  const enableNotificationsAutomatically = async (userId: string) => {
    try {
      // Check if notifications are supported
      if (!checkNotificationSupport()) {
        authLogger.debug('Push notifications not supported in this browser');
        return;
      }

      // Check current permission status
      const currentPermission = Notification.permission;

      // If permission was previously denied, we can't enable it automatically
      // But we'll still try to register background sync if they have a subscription
      if (currentPermission === 'denied') {
        authLogger.warn('Notification permission previously denied - cannot auto-enable');
        // Still try to register background sync in case they have an existing subscription
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.ready;
            await registerBackgroundSync(registration);
          } catch (error) {
            authLogger.debug('Failed to register background sync:', error);
          }
        }
        return;
      }

      // Check if user already has notifications enabled
      try {
        const statusResponse = await api.get('/api/notifications/push-status');
        if (statusResponse.data.isSubscribed) {
          authLogger.debug('User already has push notifications enabled');
          // Still register background sync even if already subscribed
          if ('serviceWorker' in navigator) {
            try {
              const registration = await navigator.serviceWorker.ready;
              await registerBackgroundSync(registration);
            } catch (error) {
              authLogger.debug('Failed to register background sync:', error);
            }
          }
          return;
        }
      } catch (error) {
        // Continue with setup if status check fails
      }

      // Automatically request permission without user prompt (required for work)
      let hasPermission = currentPermission === 'granted';
      if (!hasPermission) {
        authLogger.debug('Automatically requesting notification permission (required for work app)');
        // Request permission automatically - no user prompt needed since it's required
        hasPermission = await requestNotificationPermission();
      }

      if (!hasPermission) {
        authLogger.warn('Notification permission not granted - notifications are required for work app');
        return;
      }

      // Subscribe to push notifications automatically
      const subscribed = await subscribeToPushNotifications();
      if (subscribed) {
        // Register background sync to keep service worker alive (critical for iOS)
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.ready;
            await registerBackgroundSync(registration);
          } catch (error) {
            authLogger.debug('Failed to register background sync:', error);
          }
        }

        // Update user preferences in database to enable notifications
        try {
          await api.post('/api/notifications/preferences', {
            preferences: {
              pushNotifications: true,
              metricReminders: true,
              meetingReminders: true,
              overdueAlerts: true,
              reminderTiming: 60
            }
          });
          authLogger.debug('Successfully auto-enabled push notifications (required for work)');
        } catch (prefError) {
          authLogger.warn('Failed to update notification preferences:', prefError);
        }
      } else {
        authLogger.warn('Failed to subscribe to push notifications - notifications are required for work app');
      }
    } catch (error) {
      authLogger.error('Error during automatic notification setup (required for work):', error);
    }
  };

  useEffect(() => {
    const clearRetryTimer = () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    const validateSession = async () => {
      // 🏢 ENTERPRISE: Check server-side session via session endpoint
      // This endpoint returns 200 OK with { user: null } if not logged in, avoiding 401 console errors
      try {
        const response = await api.get('/api/auth/session', {
          timeout: 20000 // Longer timeout for initial auth check
        });

        // If user is null, we are not logged in - but it's a valid response (200 OK)
        if (!response.data.user) {
          authLogger.debug('No active session found, attempting silent refresh');
          const authService = (await import('@/services/authService')).default;
          const refreshSuccess = await authService.silentRefresh();
          
          if (refreshSuccess) {
            // Re-fetch session
            const retryResponse = await api.get('/api/auth/session', { timeout: 20000 });
            if (retryResponse.data.user) {
              setUser(retryResponse.data.user);
              setStore(retryResponse.data.store);
              syncSentryContext(retryResponse.data.user, retryResponse.data.store);
              setHasNetworkIssue(false);
              retryAttemptRef.current = 0;
              clearRetryTimer();
              setIsLoading(false);
              
              if (retryResponse.data.user?._id) {
                setTimeout(async () => {
                  try {
                    await enableNotificationsAutomatically(retryResponse.data.user._id);
                  } catch (e) {}
                }, 2000);
              }
              return;
            }
          }

          authLogger.debug('Clean exit after refresh attempt');
          SecureStorage.clearTokens();
          setUser(null);
          setSentryUser(null);
          setHasNetworkIssue(false);
          retryAttemptRef.current = 0;
          clearRetryTimer();
          setIsLoading(false);
          return;
        }

        setUser(response.data.user);
        setStore(response.data.store);

        syncSentryContext(response.data.user, response.data.store);
        setHasNetworkIssue(false);
        retryAttemptRef.current = 0;
        clearRetryTimer();

        // 🔔 AUTOMATIC PUSH NOTIFICATIONS: Enable notifications automatically when user is already logged in
        // 🔧 FIX DEC 2024: Force re-sync subscriptions that were lost from database
        if (response.data.user?._id) {
          setTimeout(async () => {
            try {
              // Check if we need a one-time forced re-sync (subscriptions were lost)
              const forceResyncKey = 'push_resync_v2';
              const hasForceResynced = localStorage.getItem(forceResyncKey);

              if (!hasForceResynced && Notification.permission === 'granted') {
                authLogger.info('🔧 Force re-syncing push subscription to server...');
                // Force re-subscribe to push notifications (sends subscription to server)
                const subscribed = await subscribeToPushNotifications();
                if (subscribed) {
                  localStorage.setItem(forceResyncKey, 'true');
                  authLogger.info('✅ Push subscription force re-synced successfully');
                } else {
                  authLogger.warn('⚠️ Push subscription force re-sync failed');
                }
              } else {
                await enableNotificationsAutomatically(response.data.user._id);
              }
            } catch (notificationError) {
              authLogger.debug('Auto-notification setup failed (non-critical):', notificationError);
            }
          }, 2000); // Delay to ensure UI is ready
        }

        // Fetch subscription status
        try {
          const subscriptionResponse = await api.get('/api/subscriptions/status');
          setSubscriptionStatus(subscriptionResponse.data.subscriptionStatus);

          // Show overlay if subscription is expired
          if (subscriptionResponse.data.subscriptionStatus === 'expired') {
            setShowExpiredOverlay(true);
          }
        } catch (subscriptionError) {
          authLogger.error('Failed to fetch subscription status');
          // Don't show overlay on subscription fetch error
        }
      } catch (error: any) {
        const _castErr = error as { response?: { status?: number; data?: { message?: string; [k: string]: unknown }; }; message?: string; code?: string; config?: any; };
        // Try silent refresh first before giving up
        const errorCode = _castErr.response?.data?.code;
        if (errorCode === 'TOKEN_EXPIRED' ||
          errorCode === 'NO_TOKEN' ||
          errorCode === 'INVALID_TOKEN' ||
          errorCode === 'TOKEN_VERSION_MISMATCH') {
          authLogger.debug('Initial auth failed, attempting silent refresh');
          const refreshSuccess = await authService.silentRefresh();
          if (refreshSuccess) {
            // Retry session request after successful refresh
            try {
              const response = await api.get('/api/auth/session');

              if (!response.data.user) {
                setUser(null);
                setIsLoading(false);
                return;
              }

              setUser(response.data.user);
              setStore(response.data.store);
              syncSentryContext(response.data.user, response.data.store);
              setHasNetworkIssue(false);
              retryAttemptRef.current = 0;
              clearRetryTimer();
              setIsLoading(false);

              // 🔔 AUTOMATIC PUSH NOTIFICATIONS: Enable notifications automatically when user is already logged in
              if (response.data.user?._id) {
                setTimeout(async () => {
                  try {
                    await enableNotificationsAutomatically(response.data.user._id);
                  } catch (notificationError) {
                    authLogger.debug('Auto-notification setup failed (non-critical):', notificationError);
                  }
                }, 2000); // Delay to ensure UI is ready
              }

              return;
            } catch (retryError) {
              authLogger.warn('Session request failed after successful refresh');
            }
          }
        }

        // 🏢 ENTERPRISE: Only clear tokens on confirmed authentication failures
        if (_castErr.response && (_castErr.response.status === 401 || _castErr.response.status === 403)) {
          authLogger.auth('No valid session found - authentication required');
          SecureStorage.clearTokens();
          setUser(null);
          setSentryUser(null); // Clear Sentry user context
          setHasNetworkIssue(false);
          retryAttemptRef.current = 0;
          clearRetryTimer();
        } else {

          // For network errors, don't clear tokens - might be temporary network issue
          // BUT: If we are on the login page, we shouldn't retry indefinitely even for network errors.
          // This also handles cases where 401s are masked as network errors (CORS).
          const isLoginPage = window.location.pathname === '/login';

          if (isLoginPage) {
            authLogger.debug('Session validation failed on login page - stopping retries');
            setHasNetworkIssue(false);
            retryAttemptRef.current = 0;
            clearRetryTimer();
          } else {
            authLogger.warn('Failed to validate session due to network issues, will retry with backoff');
            setHasNetworkIssue(true);
            // Keep existing user if any; if user is null, keep loading and retry
            const attempt = retryAttemptRef.current;
            const delay = Math.min(60000, Math.max(3000, Math.pow(2, attempt) * 3000));
            retryAttemptRef.current = attempt + 1;
            clearRetryTimer();
            retryTimerRef.current = setTimeout(() => {
              validateSession();
            }, delay);
          }
        }
        // Don't redirect here - let the user stay on current page if it's public
      }
      // Always end initial loading state so UI can render reconnect/offline states
      setIsLoading(false);
    };

    validateSession();

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  // Handle auth service events
  useEffect(() => {
    const handleTokenRefreshed = (event: CustomEvent) => {
      authLogger.debug('Token refreshed, updating user data');
      if (event.detail?.user) {
        setUser(event.detail.user);
      }
    };

    const isPublicRoute = (pathname: string) => {
      if (!pathname) return false;
      // Core public auth/marketing pages
      if (
        pathname === '/' ||
        pathname === '/login' ||
        pathname === '/register' ||
        pathname === '/forgot-password' ||
        pathname === '/reset-password'
      ) {
        return true;
      }
      // Anonymous survey links should remain accessible without auth
      if (pathname.startsWith('/survey/')) {
        return true;
      }
      return false;
    };

    const handleAuthRequired = () => {
      const currentPath = window.location.pathname || '/';

      // If we're on a public/anonymous route (landing, auth pages, survey links),
      // don't force-redirect to login. Let the route render normally.
      if (isPublicRoute(currentPath)) {
        authLogger.auth(`Authentication required event received on public route (${currentPath}) - not redirecting to login`);
        return;
      }

      authLogger.auth('Authentication required - redirecting to login');
      // Only redirect if we're not already on the login page to prevent infinite loops
      if (currentPath !== '/login') {
        // Soft sign-out: do not call server logout; keep refresh cookie intact
        window.location.href = '/login';
      }
    };

    window.addEventListener('auth:token-refreshed', handleTokenRefreshed as EventListener);
    window.addEventListener('auth:required', handleAuthRequired);

    return () => {
      window.removeEventListener('auth:token-refreshed', handleTokenRefreshed as EventListener);
      window.removeEventListener('auth:required', handleAuthRequired);
    };
  }, []);

  // Start Sentry Session Replay only once someone is signed in (and the browser
  // is idle). Keeps the replay DOM snapshot off the login page's first paint.
  useEffect(() => {
    if (user) startSentryReplay();
  }, [user]);

  // Sync overlay with subscription status updates coming from SubscriptionContext
  useEffect(() => {
    const handleSubscriptionStatus = (event: CustomEvent) => {
      const status = (event as any)?.detail?.subscriptionStatus as
        'active' | 'expired' | 'trial' | 'none' | undefined;
      if (!status) return;
      setSubscriptionStatus(status);
      if (status === 'active' || status === 'trial') {
        setShowExpiredOverlay(false);
      } else if (status === 'expired') {
        setShowExpiredOverlay(true);
      }
    };

    window.addEventListener('subscription:status-updated', handleSubscriptionStatus as EventListener);
    return () => {
      window.removeEventListener('subscription:status-updated', handleSubscriptionStatus as EventListener);
    };
  }, []);


  // 🏢 ENTERPRISE: Simplified session validation - auth service handles automatic refresh
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && user) {
        // Check for inactivity and validate push subscription (non-blocking)
        try {
          await checkInactivityAndValidateSubscription();
        } catch (error) {
          authLogger.warn('Error checking inactivity and validating push subscription:', error);
        }
        // Auth service handles token refresh automatically
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);


  const login = async (email: string, password: string, rememberMe?: boolean) => {
    // 🏢 ENTERPRISE: Clear any existing client-side auth state
    SecureStorage.clearTokens();
    setUser(null);

    try {
      const response = await api.post('/api/auth/login', {
        email,
        password,
        rememberMe: rememberMe || false
      });

      if (!response.data.success || !response.data.user) {
        throw new Error('Invalid login response from server');
      }

      // 🏢 ENTERPRISE: Server-side session management
      // No client-side token handling - httpOnly cookies handle authentication
      // This is immune to XSS attacks and follows enterprise security standards

      setUser(response.data.user);
      setStore(response.data.store);

      syncSentryContext(response.data.user, response.data.store);

      // Reset auth phase to re-enable error toasts after successful login
      resetAuthPhase();

      // Store session duration info for user feedback
      if (response.data.sessionDuration) {
        sessionStorage.setItem('sessionDuration', response.data.sessionDuration);

        // Show user-friendly notification about session duration
        authLogger.auth(`Login successful - your session will last ${response.data.sessionDuration}`);

        // You could also show a toast notification here if desired
        // toast({
        //   title: "Welcome back!",
        //   description: `Your session will last ${response.data.sessionDuration}`,
        //   duration: 5000,
        // });
      }

      // Reset validation timer on successful login
      sessionStorage.setItem('lastSessionValidation', Date.now().toString());

      // 🔔 AUTOMATIC PUSH NOTIFICATIONS: Enable notifications automatically for employees
      setTimeout(async () => {
        try {
          await enableNotificationsAutomatically(response.data.user._id);
        } catch (notificationError) {
          authLogger.debug('Auto-notification setup failed (non-critical):', notificationError);
        }
      }, 2000); // Delay to ensure UI is ready

      // Fetch subscription status after login
      try {
        const subscriptionResponse = await api.get('/api/subscriptions/status');
        setSubscriptionStatus(subscriptionResponse.data.subscriptionStatus);

        // Show overlay if subscription is expired
        if (subscriptionResponse.data.subscriptionStatus === 'expired') {
          setShowExpiredOverlay(true);
        }
      } catch (subscriptionError) {
        authLogger.error('Failed to fetch subscription status after login');
        // Don't show overlay on subscription fetch error
      }

      return response.data;
    } catch (error: any) {
      // Ensure auth state is cleared on login error
      SecureStorage.clearTokens();
      setUser(null);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // 🏢 ENTERPRISE: Call server logout to clear httpOnly session cookie
      await api.post('/api/auth/logout');
    } catch (error) {
      // Continue with logout even if server call fails
      authLogger.error('Server logout failed, continuing with client logout');
    }

    // Clear any remaining client-side auth state
    SecureStorage.clearTokens();
    setUser(null);
    setSentryUser(null); // Clear Sentry user context

    // 🔒 SECURITY: Clear service worker cache on logout
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      const messageChannel = new MessageChannel();
      navigator.serviceWorker.controller.postMessage(
        { type: 'CLEAR_AUTH_CACHE' },
        [messageChannel.port2]
      );
    }

    // Redirect to login page after logout
    window.location.href = '/login';
  };

  // Switch store function - available for users with multi-store access
  const switchStore = async (storeId: string) => {
    try {
      const response = await userStoreService.switchStore(storeId);

      // 🏢 ENTERPRISE: Server handles session updates via httpOnly cookies
      // No client-side token management needed
      // The switch payload only carries a subset of User fields (no isAdmin,
      // accessLevel, etc.) and the store lives on response.user.store, so merge
      // into the existing session state instead of replacing it wholesale.
      const switchedStore = response.user.store;
      setUser(prev => (prev ? { ...prev, ...response.user } : prev));
      setStore(prev => (prev ? { ...prev, ...switchedStore } : prev));
      syncSentryContext(response.user, switchedStore);

      // Fetch subscription status after switching store
      try {
        const subscriptionResponse = await api.get('/api/subscriptions/status');
        setSubscriptionStatus(subscriptionResponse.data.subscriptionStatus);

        // Show overlay if subscription is expired
        if (subscriptionResponse.data.subscriptionStatus === 'expired') {
          setShowExpiredOverlay(true);
        }
      } catch (subscriptionError) {
        authLogger.error('Failed to fetch subscription status after switching store');
        // Don't show overlay on subscription fetch error
      }

      return response;
    } catch (error: any) {
      authLogger.error('Failed to switch store');
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        store,
        login,
        logout,
        switchStore,
        isLoading,
        hasNetworkIssue,
        subscriptionStatus,
        dismissExpiredOverlay
      }}
    >
      {children}
      {showExpiredOverlay && (
        <ExpiredSubscriptionOverlay
          onClose={dismissExpiredOverlay}
          user={user}
          store={store}
        />
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};